import React, { createContext, useState, useEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { PostTransportAndJourney } from '../../api/GBENSDec';
import { getErrorsDetails } from '../../api/dashboard';
import Stepper from '../../components/Stepper';
import { READ_ONLY_LABEL } from '../../utils/constants';

import dayjs from 'dayjs';

//Adapter JS Imports
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

//--------------------Material UI Imports----------------------
import { Container } from '@mui/material';

//--------------------Screen Imports----------------------
import TransportAndJourney from './Screens/1. TransportAndJourney';
import Consignments from './Screens/2. Consignments';
import AddingConsignment from './Screens/2.1. Adding Consignment';
import ReviewAndSubmit from './Screens/3. Review and Submit';

import FixOrContinueModal from './Modals/FixOrContinueModal';
import ReviewAndSubmitMenu from './ReviewAndSubmitMenu';

//--------------------Hook Imports----------------------
import useErrors from './Hooks/useErrors';
import useData from './Hooks/useData';
import useConsignment from './Hooks/useConsignment';
import useReferenceData from './Hooks/useReferenceData';

//-------------------HMRC Error Imports----------------------
import ShipmentErrorBox from '../../components/ShipmentErrorBox';
import ShipmentErrorModal from '../../components/ShipmentErrorModal';
import { canAddConsignment, getAmendOrNot, getShipmentDeleteOrCancel } from '../../api/util';

export const GBENSContext = createContext(null);

const GBENSContainer = () => {
  const { state } = useLocation();

  const [headerId, setHeaderId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [cancelOrDelete, setCancelOrDelete] = useState('');
  const [isAmendable, setIsAmendable] = useState(false);
  const [possibleToAddConsignment, setPossibleToAddConsignment] = useState({ isValid: 0, message: '' });
  const [showSubmitButton, setShowSubmitButton] = useState(true);

  const [navToConsignmentId, setNavToConsignmentId] = useState(null);

  //--------------------HMRC Error Dialog----------------------
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorData, setErrorData] = useState([]);

  //--------------------Modal state stored in container to allow access in multiple places----------------------
  const [deleteConsignmentModalOpen, setDeleteConsignmentModalOpen] = useState(false);
  const [deleteConsignmentRowsSelected, setDeleteConsignmentRowsSelected] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [cancelShipmentModalOpen, setCancelShipmentModalOpen] = useState(false);
  const [cannotCancelShipmentModalOpen, setCannotCancelShipmentModalOpen] = useState(false);
  const [amendShipmentModalOpen, setAmendShipmentModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  //--------------------Required here to avoid circular dependency----------------------
  const [FixOrContinueModalOpen, setFixOrContinueModalOpen] = useState(false);
  const [toRunInitialErrorCheck, setToRunInitialErrorCheck] = useState(false);
  const [goToNextStepFunc, setGoToNextStepFunc] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [errorsChecked, setErrorsChecked] = useState(false);
  const [isEditable, setIsEditable] = useState(true); // whether the header is editable or not (does not include consignments)
  const [isReadOnly, setIsReadOnly] = useState(false); // whether the whole form should be read only

  const [consignmentsVisited, setConsignmentsVisited] = useState(false);
  const [currentConsignmentErrors, setCurrentConsignmentErrors] = useState(null);
  const [currentConsignmentHMRCErrors, setCurrentConsignmentHMRCErrors] = useState(null);
  const [currentConsignment, setCurrentConsignment] = useState(null);
  const [EUROShuttleDisableFields, setEUROShuttleDisableFields] = useState(false);

  //--------------------Data----------------------
  const { data, setData, updateData, moveData, deleteData } = useData(
    state,
    setHeaderId,
    setCompanyId,
    setToRunInitialErrorCheck
  );
  //--------------------Reference Data----------------------
  const { referenceData, refreshNeenors } = useReferenceData(companyId);

  //--------------------Errors----------------------
  const {
    errors,
    setErrors,
    errorsLocal,
    setErrorsLocal,
    errorsHMRC,
    setErrorsHMRC,
    checkErrors,
    checkErrorsStandard,
    checkErrorsLocal,
    checkErrorsConsignment,
    checkErrorsConsignmentLocal,
    hasErrors,
    handleShowWarningModal,
    allValid,
    isLocked,
    isAccompanied,
    tabValidationErrors,
    HMRCtabValidationErrors,
    currentConsignmentHasData,
    initialErrorCheckRun,
  } = useErrors(
    state,
    referenceData,
    toRunInitialErrorCheck,
    setToRunInitialErrorCheck,
    setGoToNextStepFunc,
    setActiveStep,
    errorsChecked,
    setErrorsChecked,
    consignmentsVisited,
    currentConsignmentErrors,
    setCurrentConsignmentErrors,
    currentConsignmentHMRCErrors,
    setCurrentConsignmentHMRCErrors,
    currentConsignment,
    data,
    isEditable
  );

  //--------------------Consignment----------------------
  const {
    addNewConsignment,
    editConsignment,
    updateCurrentConsignment,
    saveCurrentConsignment,
    moveConsignmentData,
    setBlankConsignmentData,
    selectNeenor,
    deleteConsignmentData,
    addNewConsignmentLine,
    updateCurrentLine,
    editConsignmentLine,
    saveConsignmentLine,
    deleteConsignmentLine,
    deleteConsignment,
    hasConsignments,
    currentLine,
    handleGetMRNDetails,
  } = useConsignment(
    currentConsignment,
    setCurrentConsignment,
    currentConsignmentErrors,
    setCurrentConsignmentErrors,
    currentConsignmentHMRCErrors,
    setCurrentConsignmentHMRCErrors,
    data,
    setData,
    errors,
    setErrors,
    errorsHMRC,
    setErrorsHMRC,
    headerId,
    refreshNeenors,
    referenceData
  );

  //--------------------Navigate to consignment from dashboard----------------------
  useEffect(() => {
    if (state?.consignmentId && navToConsignmentId === null && initialErrorCheckRun) {
      setNavToConsignmentId(state.consignmentId);
    }
  }, [state, initialErrorCheckRun]);

  //--------------------Check if fields should be readonly ------------------------------
  useEffect(() => {
    setIsReadOnly(state.isReadOnly);
  }, [state.isReadOnly]);

  //-------------------- Check if we should hide the submit button ----------------------
  useEffect(() => {
    // amended shipments cannot be resubmitted, so hide the button
    const hideButton = data?.TransportAndJourney?.statusDescription?.toUpperCase?.() === 'AMENDED';
    setShowSubmitButton(!hideButton);
  }, [data?.TransportAndJourney?.statusDescription]);

  //--------------------Check for errors before moving to next step----------------------
  useEffect(() => {
    if (goToNextStepFunc && errorsChecked) {
      if (hasErrors(activeStep) && (activeStep !== 'AddingConsignment' || currentConsignmentHasData))
        setFixOrContinueModalOpen(true);
      else if (
        activeStep === 'uploadConsignment' ||
        activeStep === 'ReviewAndSubmit' ||
        !hasErrors(activeStep) ||
        (hasErrors(activeStep) && activeStep === 'AddingConsignment' && !currentConsignmentHasData)
      ) {
        onContinue();
      }
      setErrorsChecked(false);
    }
  }, [goToNextStepFunc, errorsChecked, hasErrors, activeStep]);

  //--------------------Save data before moving to next step----------------------
  const onContinueTransportAndJourney = async () => {
    let transportData = { ...data['TransportAndJourney'] };

    if (transportData.arrivalDateTime)
      transportData.arrivalDateTime = dayjs(transportData.arrivalDateTime).format('YYYY-MM-DDTHH:mm:ss.SSS');

    if (transportData.departureDateTime)
      transportData.departureDateTime = dayjs(transportData.departureDateTime).format('YYYY-MM-DDTHH:mm:ss.SSS');

    // temporary fix until we make the modals for these consistent
    if (transportData.Consignor) transportData.Vehicle.Consignor = transportData.Consignor;
    if (transportData.Consignee) transportData.Vehicle.Consignee = transportData.Consignee;

    if (transportData.portOfDeparture) {
      const departureOptionValue = parseInt(transportData.portOfDeparture);
      if (departureOptionValue) {
        transportData.portOfDeparture = departureOptionValue;
        transportData.portOfDepartureFreeText = null;
      } else {
        transportData.portOfDepartureFreeText = transportData.portOfDeparture;
        transportData.portOfDeparture = null;
      }
    } else {
      // if port of departure is not present when saving, then we need to make sure to reset the free text field also
      transportData.portOfDepartureFreeText = null;
    }

    // structure place of loading .etc fields
    transportData = {
      ...transportData,
      PlaceOfLoading: {
        country: transportData.placeOfLoadingCountry,
        placeName: transportData.placeOfLoadingName,
      },
      PlaceOfUnloading: {
        country: transportData.placeOfUnloadingCountry,
        placeName: transportData.placeOfUnloadingName,
      },
      PlaceOfAcceptance: {
        country: transportData.placeOfAcceptanceCountry,
        placeName: transportData.placeOfAcceptanceName,
      },
      PlaceOfDelivery: {
        country: transportData.placeOfDeliveryCountry,
        placeName: transportData.placeOfDeliveryName,
      },
    };

    let dt;
    if (!headerId) {
      dt = await PostTransportAndJourney({ TransportAndJourney: transportData }, companyId);
    } else {
      dt = await PostTransportAndJourney({ TransportAndJourney: { headerId, ...transportData } }, companyId);
    }

    if (dt?.[0]?.headerId) {
      setHeaderId(dt[0].headerId);
    } else throw new Error('Error saving headers');
  };

  const onContinue = async () => {
    if ((currentConsignmentHasData && activeStep === 'AddingConsignment') || activeStep === 'UpdatingConsignment') {
      await saveCurrentConsignment();
    } else if (activeStep === 'TransportAndJourney') {
      await onContinueTransportAndJourney();
    }
    setFixOrContinueModalOpen(false);
    goToNextStepFunc();
    setGoToNextStepFunc(false);
  };

  //--------------------Show HMRC errors----------------------
  const showErrorModal = () => {
    setErrorDialogOpen(true);
    getErrorsDetails(state.headerId)
      .then((data) => {
        setErrorData(data);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  //--------------------Get Consignment Details from XML----------------------

  const handleGetXMLDetails = (xmlData) => {
    const data = xmlData.Consignments;
    const e = { lines: [] };
    setCurrentConsignmentErrors(e);
    const e2 = { lines: [] };
    setCurrentConsignmentHMRCErrors(e2);
    handleGetMRNDetails(data);
  };

  //--------------------Clear selection from delete consignment modal----------------------
  useEffect(() => {
    setDeleteConsignmentRowsSelected(false);
  }, [deleteConsignmentModalOpen]);

  useEffect(() => {
    const fetchShipmentData = async (headerId) => {
      try {
        const [cancelDeleteResponse, amendResponse, canAddConsignmentResponse] = await Promise.all([
          getShipmentDeleteOrCancel(headerId),
          getAmendOrNot(headerId),
          canAddConsignment(headerId),
        ]);
        const { cancelShipment, deleteShipment } = cancelDeleteResponse[0];
        let action = 'none';
        if (cancelShipment) {
          action = 'cancel';
        } else if (deleteShipment) {
          action = 'delete';
        }
        setCancelOrDelete(action);
        setIsAmendable(!!amendResponse[0]['ammendAllowed']);
        setPossibleToAddConsignment(canAddConsignmentResponse[0]);
      } catch (error) {
        console.error('Error fetching shipment data:', error);
      }
    };

    if (headerId) {
      fetchShipmentData(headerId);
    }
  }, [headerId]);

  useEffect(() => {
    setIsEditable(!!data.TransportAndJourney.isEditable);
  }, [data]);

  const providerValue = useMemo(
    () => ({
      headerId,
      setHeaderId,
      companyId,
      setCompanyId,
      navToConsignmentId,
      setNavToConsignmentId,
      isEditable,
      isReadOnly,
      showSubmitButton,
      EUROShuttleDisableFields,
      setEUROShuttleDisableFields,

      //Modals
      deleteConsignmentModalOpen,
      setDeleteConsignmentModalOpen,
      deleteConsignmentRowsSelected,
      setDeleteConsignmentRowsSelected,
      duplicateModalOpen,
      setDuplicateModalOpen,
      cancelShipmentModalOpen,
      setCancelShipmentModalOpen,
      cannotCancelShipmentModalOpen,
      setCannotCancelShipmentModalOpen,
      submitModalOpen,
      setSubmitModalOpen,
      amendShipmentModalOpen,
      setAmendShipmentModalOpen,

      //Required to avoid circular dependency
      currentConsignmentErrors,
      currentConsignmentHMRCErrors,
      currentConsignment,
      setCurrentConsignment,

      //Reference Data
      referenceData,

      //Data
      data,
      setData,
      updateData,
      moveData,
      deleteData,

      //Errors
      errors,
      setErrors,
      errorsLocal,
      setErrorsLocal,
      errorsHMRC,
      checkErrors,
      checkErrorsStandard,
      checkErrorsLocal,
      checkErrorsConsignment,
      checkErrorsConsignmentLocal,
      hasErrors,
      handleShowWarningModal,
      allValid,
      isAccompanied,
      isLocked,

      //Consignments
      addNewConsignment,
      editConsignment,
      updateCurrentConsignment,
      saveCurrentConsignment,
      moveConsignmentData,
      setBlankConsignmentData,
      selectNeenor,
      deleteConsignmentData,
      addNewConsignmentLine,
      updateCurrentLine,
      editConsignmentLine,
      saveConsignmentLine,
      deleteConsignmentLine,
      deleteConsignment,
      hasConsignments,
      currentLine,
      cancelOrDelete,
      isAmendable,
      possibleToAddConsignment,
    }),
    [
      headerId,
      setHeaderId,
      companyId,
      setCompanyId,
      navToConsignmentId,
      setNavToConsignmentId,
      EUROShuttleDisableFields,
      setEUROShuttleDisableFields,

      //Modals
      deleteConsignmentModalOpen,
      setDeleteConsignmentModalOpen,
      deleteConsignmentRowsSelected,
      setDeleteConsignmentRowsSelected,
      duplicateModalOpen,
      setDuplicateModalOpen,
      cancelShipmentModalOpen,
      setCancelShipmentModalOpen,
      cannotCancelShipmentModalOpen,
      setCannotCancelShipmentModalOpen,
      submitModalOpen,
      setSubmitModalOpen,
      amendShipmentModalOpen,
      setAmendShipmentModalOpen,

      //Required to avoid circular dependency
      currentConsignmentErrors,
      currentConsignmentHMRCErrors,
      currentConsignment,
      setCurrentConsignment,

      //Reference Data
      referenceData,

      //Data
      data,
      setData,
      updateData,
      moveData,
      deleteData,

      //Errors
      errors,
      setErrors,
      errorsLocal,
      setErrorsLocal,
      errorsHMRC,
      checkErrors,
      checkErrorsStandard,
      checkErrorsLocal,
      checkErrorsConsignmentLocal,
      hasErrors,
      handleShowWarningModal,
      allValid,
      isLocked,
      isAccompanied,

      //Consignments
      addNewConsignment,
      editConsignment,
      updateCurrentConsignment,
      saveCurrentConsignment,
      moveConsignmentData,
      setBlankConsignmentData,
      selectNeenor,
      deleteConsignmentData,
      addNewConsignmentLine,
      updateCurrentLine,
      editConsignmentLine,
      saveConsignmentLine,
      deleteConsignmentLine,
      deleteConsignment,
      hasConsignments,
      currentLine,
      cancelOrDelete,
      isAmendable,
      possibleToAddConsignment,
    ],
    { deep: true }
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <GBENSContext.Provider value={providerValue}>
        <FixOrContinueModal
          open={FixOrContinueModalOpen}
          onFix={() => setFixOrContinueModalOpen(false)}
          onSave={onContinue}
        />

        {state?.image && (
          <div className="declarations-header">
            <div
              className="declarations-header-image"
              style={{ backgroundImage: `url('data:image/png;base64,${state.image}')` }}
            />
            <div className="declarations-header-company">
              {state?.currentCompany?.companyName} {state?.currentCompany?.companyEori}{' '}
              {isReadOnly && <>{READ_ONLY_LABEL}</>}
            </div>
          </div>
        )}

        {!state?.image && (
          <div className="declarations-header-company with-no-image">
            {state?.currentCompany?.companyName} {state?.currentCompany?.companyEori}{' '}
            {isReadOnly && <>{READ_ONLY_LABEL}</>}
          </div>
        )}

        <Stepper
          defaultStepKey={state?.activeStep || 'TransportAndJourney'}
          showFuture={true}
          canSkipForward={true}
          stepClassName="business-partner-section"
          useModalToChangeTabs={!isReadOnly}
          handleShowWarningModal={handleShowWarningModal}
          tabValidation={tabValidationErrors}
          hmrcTabValidation={HMRCtabValidationErrors}
        >
          <Container
            className="declaration-container"
            maxWidth="xl"
            sx={{
              p: 4,
              mt: 0,
              mb: 6,
            }}
          >
            <div className="hide-stepper-icons">
              {' '}
              {/* Side Menus */}
              {state.error && (
                <div style={{ width: '320px', paddingBottom: '20px' }}>
                  <ShipmentErrorBox isError={state.error} isRejected={false} showErrorModal={showErrorModal} />
                </div>
              )}
              <Stepper.StepList
                isSticky={true}
                title={<div className="declaration-stepper-header">Create an ENS Declaration</div>}
              />
              <Stepper.ShowOnStep stepKey="ReviewAndSubmit">
                <ReviewAndSubmitMenu />
              </Stepper.ShowOnStep>
            </div>

            <Stepper.Step text="1. Transport and Journey" stepKey="TransportAndJourney">
              <TransportAndJourney />
            </Stepper.Step>
            <Stepper.Step text="2. Consignments" stepKey="Consignments">
              <Consignments setConsignmentsVisited={setConsignmentsVisited} />
            </Stepper.Step>
            <Stepper.Step subStep text="Adding Consignment" stepKey="AddingConsignment">
              <AddingConsignment handleGetMRNDetails={handleGetMRNDetails} />
            </Stepper.Step>
            <Stepper.Step subStep text="Updating Consignment" stepKey="UpdatingConsignment">
              <AddingConsignment handleGetMRNDetails={handleGetMRNDetails} isUpdating />
            </Stepper.Step>
            <Stepper.Step text="3. Review and Submit" stepKey="ReviewAndSubmit">
              <ReviewAndSubmit />
            </Stepper.Step>
            <ShipmentErrorModal open={errorDialogOpen} setOpen={setErrorDialogOpen} errorData={errorData} />
          </Container>
        </Stepper>
      </GBENSContext.Provider>
    </LocalizationProvider>
  );
};

export default GBENSContainer;
